// src/features/ceiling/CeilingBar.tsx
import React, { useMemo } from "react";
import { Group, Text, Divider, ActionIcon, Badge, Tooltip, Button, Stack } from "@mantine/core";
import { FaHistory } from "react-icons/fa";
import { FaBorderAll, FaTableCells } from "react-icons/fa6";
import { skipToken } from "@reduxjs/toolkit/query";
import { ModuleToolBarShell } from "../ModuleToolBarShell";
import { ActivateSnapSwitch } from "./ActivateSnapSwitch";
import { ActivateTrueSizeSwitch } from "./ActivateTrueSizeSwitch";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { SelectedRoomsActionsMenu } from "../../devices/SelectedRooms/SelectedRoomsActionsMenu";
import { SelectedRoomsValidationSummaryInline } from "../../devices/SelectedRooms/SelectedRoomsValidationSummaryInline";
import { selectDeviceHistory } from "../../history/deviceHistorySlice";
import { DesignLeftSideBarTabs } from "@multiconsult-group/multiplacer-core";
import {
  setShowCeilingFreeTilesForFloorInRedux,
  setActiveLeftSideBarTabInRedux,
  toggleCeilingIfcClassesContextInRedux,
} from "../../designSlice";
import { thunkAutoPipeRoutingWithMockData } from "../../routing/autoPipeRouting/autoPipeRoutingThunks";
import { clearUnsavedRoutingItems } from "../../routing/unsavedRoutingSlice";
import { AutoPipeRoutingRunProgress } from "../../routing/autoPipeRouting/AutoPipeRoutingRunProgress";
import { usePostMultiplacerRoomsCeilingFreeTilesByFloorQuery } from "../../../../openapi";
import {
  selectActiveCeilingFreeTilesByFloorArgs,
  selectCeilingFreeTilesByFloorArgs,
  selectHttpCeilingFreeTilesByFloorResult,
} from "../../../Room/httpRoomSelectors";

export const CeilingBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const isDev = import.meta.env.DEV;

  const selectedRooms = useAppSelector((s) => s.design.selectedRooms);
  const hasSelectedRooms = (selectedRooms?.length ?? 0) > 0;

  const runSpaceValidationsInSelectedRooms = useAppSelector(
    (s) => s.design.runSpaceValidationsInSelectedRooms
  );

  const history = useAppSelector(selectDeviceHistory);

  const historyCount = useMemo(() => {
    return (history.undoStack?.length ?? 0) + (history.redoStack?.length ?? 0);
  }, [history.undoStack?.length, history.redoStack?.length]);

  const canShowHistory = historyCount > 0;

  const activeLeftSideBarTab = useAppSelector((s) => s.design.activeLeftSideBarTab);
  const ceilingFreeTilesArgs = useAppSelector(selectCeilingFreeTilesByFloorArgs);
  const activeCeilingFreeTilesArgs = useAppSelector(selectActiveCeilingFreeTilesByFloorArgs);
  const ceilingFreeTilesResult = useAppSelector(selectHttpCeilingFreeTilesByFloorResult);
  const showCeilingFreeTilesForFloor = useAppSelector(
    (state) => state.design.showCeilingFreeTilesForFloor
  );
  const showCeilingIfcClassesContext = useAppSelector(
    (state) => state.design.showCeilingIfcClassesContext
  );
  const { isFetching: isLoadingCeilingFreeTiles } =
    usePostMultiplacerRoomsCeilingFreeTilesByFloorQuery(activeCeilingFreeTilesArgs ?? skipToken);
  const hasCeilingFreeTilesContext = ceilingFreeTilesArgs != null;

  const toggleHistoryPanel = () => {
    const isOpen = activeLeftSideBarTab === DesignLeftSideBarTabs.DEVICE_HISTORY;
    dispatch(
      setActiveLeftSideBarTabInRedux(isOpen ? undefined : DesignLeftSideBarTabs.DEVICE_HISTORY)
    );
  };

  const toggleCeilingFreeTilesForFloor = () => {
    if (!ceilingFreeTilesArgs) return;

    dispatch(setShowCeilingFreeTilesForFloorInRedux(!showCeilingFreeTilesForFloor));
  };

  return (
    <ModuleToolBarShell>
      <Stack gap="sm" w="100%">
        <Group align="center" gap="md">
          {/* Tittel / kontekst */}
          <Group gap="sm" align="center">
            <Text fw={600} size="sm">
              Himlingsdesign
            </Text>
            <Divider orientation="vertical" h={24} />
          </Group>

          {/* Verktøy: snap to grid */}
          <ActivateSnapSwitch />
          <ActivateTrueSizeSwitch />
          <Tooltip label="Vis IFC-klasser for himling, rom og møbler" withArrow>
            <ActionIcon
              variant={showCeilingIfcClassesContext ? "filled" : "outline"}
              size="sm"
              color="blue"
              onClick={() => dispatch(toggleCeilingIfcClassesContextInRedux())}
              aria-label="Vis IFC-klasser for himling, rom og møbler"
              aria-pressed={showCeilingIfcClassesContext}
            >
              <FaTableCells size={15} />
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={
              hasCeilingFreeTilesContext
                ? "Vis alle ledige himlingsplater i etasjen"
                : "Velg etasje for å vise ledige himlingsplater"
            }
            withArrow
          >
            <ActionIcon
              variant={showCeilingFreeTilesForFloor ? "filled" : "outline"}
              size="sm"
              color="green"
              loading={isLoadingCeilingFreeTiles}
              disabled={!hasCeilingFreeTilesContext}
              onClick={toggleCeilingFreeTilesForFloor}
              aria-label="Vis alle ledige himlingsplater"
              aria-pressed={showCeilingFreeTilesForFloor}
            >
              <FaBorderAll size={15} />
            </ActionIcon>
          </Tooltip>
          {showCeilingFreeTilesForFloor && ceilingFreeTilesResult && (
            <Badge variant="light" color="green" size="sm">
              {ceilingFreeTilesResult.tileCount}
            </Badge>
          )}

          {/* ✅ Historikk-indikator (vises kun når det finnes endringer) */}
          {canShowHistory && (
            <>
              <Divider orientation="vertical" h={24} />
              <Tooltip label={`Historikk (${historyCount})`} withArrow>
                <Group gap={8}>
                  <ActionIcon
                    variant="outline"
                    size="sm"
                    onClick={toggleHistoryPanel}
                    aria-label="Åpne historikk"
                  >
                    <FaHistory size={16} />
                  </ActionIcon>

                  <Badge
                    variant="outline"
                    size="sm"
                    style={{ cursor: "pointer" }}
                    onClick={toggleHistoryPanel}
                  >
                    {historyCount}
                  </Badge>
                </Group>
              </Tooltip>
            </>
          )}

          {/* Inline status for valgte rom */}
          {hasSelectedRooms && runSpaceValidationsInSelectedRooms && (
            <>
              <Divider orientation="vertical" h={24} />
              <SelectedRoomsValidationSummaryInline />
            </>
          )}

          {/* Actions på valgte rom (kun synlig når rom er valgt) */}
          {hasSelectedRooms && (
            <>
              <Divider orientation="vertical" h={24} />
              <SelectedRoomsActionsMenu />
            </>
          )}

          {isDev && (
            <>
              <Divider orientation="vertical" h={24} />
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    void dispatch(thunkAutoPipeRoutingWithMockData());
                  }}
                >
                  Mock Auto Pipe Routing
                </Button>
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    dispatch(clearUnsavedRoutingItems());
                  }}
                >
                  Clear Routing
                </Button>
              </Group>
            </>
          )}
        </Group>

        <AutoPipeRoutingRunProgress />
      </Stack>
    </ModuleToolBarShell>
  );
};
